import React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import { Bold, Italic, Strikethrough, Underline } from "lucide-react";

import { useInlineContentEditor } from "./hooks/useInlineContentEditor";

type InlineToolbarProps = {
  className?: string;
};

/**
 * An inline toolbar component for text editing with formatting options.
 * Provides buttons for bold, italic, underline, strikethrough formatting.
 * Text is automatically saved on blur, so no save button is needed.
 */
function InlineToolbar({ className }: InlineToolbarProps) {
  const {
    currentFormatting,
    toggleBold,
    toggleItalic,
    toggleUnderline,
    toggleStrikethrough,
  } = useInlineContentEditor();

  // TODO (Kurt, 2025-08-05): Remove the hidden class when we re-enable the toolbar.
  return (
    <div
      className={twMerge(
        "hidden items-center gap-2 bg-white border border-border rounded-lg shadow-sm p-2",
        className,
      )}
    >
      {/* Formatting buttons */}
      <Tooltip content="Bold">
        <Button
          icon
          variant="tertiary"
          size="sm"
          onClick={toggleBold}
          UNSAFE_className={twMerge(
            currentFormatting.bold && "bg-neutral-soft",
          )}
        >
          <Bold />
        </Button>
      </Tooltip>
      <Tooltip content="Italic">
        <Button
          icon
          variant="tertiary"
          size="sm"
          onClick={toggleItalic}
          UNSAFE_className={twMerge(
            currentFormatting.italic && "bg-neutral-soft",
          )}
        >
          <Italic />
        </Button>
      </Tooltip>
      <Tooltip content="Underline">
        <Button
          icon
          variant="tertiary"
          size="sm"
          onClick={toggleUnderline}
          UNSAFE_className={twMerge(
            currentFormatting.underline && "bg-neutral-soft",
          )}
        >
          <Underline />
        </Button>
      </Tooltip>
      <Tooltip content="Strikethrough">
        <Button
          icon
          variant="tertiary"
          size="sm"
          onClick={toggleStrikethrough}
          UNSAFE_className={twMerge(
            currentFormatting.strikethrough && "bg-neutral-soft",
          )}
        >
          <Strikethrough />
        </Button>
      </Tooltip>
    </div>
  );
}

export default InlineToolbar;
